import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supplyChainService } from '../services/apiClient';

// Query keys for consistent caching
export const queryKeys = {
  rawInventoryData: (warehouse: string) => ['raw-inventory-data', warehouse],
  rawSalesData: (warehouse: string) => ['raw-sales-data', warehouse],
  rawDataCombined: (warehouse: string) => ['raw-data-combined', warehouse],
  localStockManagement: (warehouse: string) => ['local-stock-management', warehouse],
  centralStockManagement: () => ['central-stock-management'],
  centralStockManagementPage: (page: number, pageSize: number, compact: boolean) => ['central-stock-management', page, pageSize, compact],
  localStockManagementPage: (warehouse: string, page: number, pageSize: number, includeSkuBreakdown: boolean) =>
    ['local-stock-management', warehouse, page, pageSize, includeSkuBreakdown],
  localStockSummary: (warehouse: string) => ['local-stock-summary', warehouse],
  centralStockSummary: () => ['central-stock-summary'],
  ordersToBePlace: () => ['orders-to-be-placed'],
  ordersInProgress: () => ['orders-in-progress'],
  shipments: () => ['shipments'],
} as const;

// Configuration for different data types
const queryConfig = {
  // Raw data doesn't change often - longer cache time  
  rawData: {
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 60 minutes
    refetchOnWindowFocus: false,
  },
  // Calculated data changes more frequently
  calculatedData: {
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
  },
  // Real-time data needs frequent updates
  realTimeData: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  },
  // Database data - shorter cache, invalidated on mutations
  databaseData: {
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  },
};

// Raw Data Hooks
export const useRawInventoryData = (warehouse: string) => {
  return useQuery({
    queryKey: queryKeys.rawInventoryData(warehouse),
    queryFn: () => supplyChainService.getRawInventoryData(warehouse),
    ...queryConfig.rawData,
    enabled: !!warehouse,
  });
};

export const useRawSalesData = (warehouse: string) => {
  return useQuery({
    queryKey: queryKeys.rawSalesData(warehouse),
    queryFn: () => supplyChainService.getRawSalesData(warehouse),
    ...queryConfig.rawData,
    enabled: !!warehouse,
  });
};

export const useRawDataCombined = (warehouse: string) => {
  return useQuery({
    queryKey: queryKeys.rawDataCombined(warehouse),
    queryFn: () => supplyChainService.getRawDataCombined(warehouse),
    ...queryConfig.rawData,
    enabled: !!warehouse,
  });
};

// Calculated Data Hooks
export const useLocalStockManagement = (warehouse: string, snapshotDate?: string) => {
  return useQuery({
    queryKey: [...queryKeys.localStockManagement(warehouse), snapshotDate ?? "latest"],
    queryFn: () => supplyChainService.getLocalStockManagement(warehouse, snapshotDate ? { snapshotDate } : undefined),
    ...queryConfig.calculatedData,
    enabled: !!warehouse,
  });
};

export const useLocalStockManagementPage = (
  warehouse: string,
  page: number,
  pageSize: number,
  includeSkuBreakdown: boolean = true
) => {
  return useQuery({
    queryKey: queryKeys.localStockManagementPage(warehouse, page, pageSize, includeSkuBreakdown),
    queryFn: () => supplyChainService.getLocalStockManagement(warehouse, { page, pageSize, includeSkuBreakdown }),
    ...queryConfig.calculatedData,
    enabled: !!warehouse,
  });
};

export const useCentralStockManagement = () => {
  return useQuery({
    queryKey: queryKeys.centralStockManagement(),
    queryFn: () => supplyChainService.getCentralStockManagement(),
    ...queryConfig.calculatedData,
  });
};

export const useCentralStockManagementPage = (
  page: number,
  pageSize: number,
  compact: boolean = true
) => {
  return useQuery({
    queryKey: queryKeys.centralStockManagementPage(page, pageSize, compact),
    queryFn: () => supplyChainService.getCentralStockManagement({ page, pageSize, compact }),
    ...queryConfig.calculatedData,
  });
};

export const useLocalStockSummary = (warehouse: string) => {
  return useQuery({
    queryKey: queryKeys.localStockSummary(warehouse),
    queryFn: () => supplyChainService.getLocalStockManagementSummary(warehouse),
    ...queryConfig.realTimeData,
    enabled: !!warehouse,
  });
};

export const useCentralStockSummary = () => {
  return useQuery({
    queryKey: queryKeys.centralStockSummary(),
    queryFn: () => supplyChainService.getCentralStockManagementSummary(),
    ...queryConfig.realTimeData,
  });
};

export const useOrdersToBePlace = () => {
  return useQuery({
    queryKey: queryKeys.ordersToBePlace(),
    queryFn: () => supplyChainService.getOrdersToBePlaced(),
    ...queryConfig.realTimeData,
  });
};

// ============================================================================
// ORDER MANAGEMENT HOOKS
// ============================================================================

// Orders In Progress 
export const useOrdersInProgress = () => {
  return useQuery({
    queryKey: queryKeys.ordersInProgress(),
    queryFn: () => supplyChainService.getOrdersInProgress(),
    ...queryConfig.databaseData,
  });
};

// Shipments - cached but invalidated on create/delete
export const useShipments = () => {
  return useQuery({
    queryKey: queryKeys.shipments(),
    queryFn: () => supplyChainService.getShipments(),
    ...queryConfig.databaseData,
  });
};

// ============================================================================
// OPTIMISTIC UPDATE MUTATIONS - UI updates INSTANTLY, API runs in background
// ============================================================================

// Mutation: Create Order - OPTIMISTIC: adds to UI immediately
export const useCreateOrder = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (orderData: any) => supplyChainService.createOrderInProgress(orderData),
    
    // OPTIMISTIC: Update UI immediately before API completes
    onMutate: async (newOrder) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.ordersInProgress() });
      // Snapshot previous value for rollback
      const previousOrders = queryClient.getQueryData(queryKeys.ordersInProgress());
      // Optimistically add new order to cache (with temp ID)
      queryClient.setQueryData(queryKeys.ordersInProgress(), (old: any) => {
        const orders = old?.orders || old || [];
        const tempOrder = {
          ...newOrder,
          order_id: Date.now(), // Temporary ID
          status: 'pending',
          created_at: new Date().toISOString(),
        };
        return { orders: [...orders, tempOrder] };
      });
      
      console.log('⚡ Optimistic: Order added to UI instantly');
      return { previousOrders };
    },
    
    // If API fails, rollback to previous state
    onError: (err, newOrder, context) => {
      console.error('❌ API failed, rolling back:', err);
      if (context?.previousOrders) {
        queryClient.setQueryData(queryKeys.ordersInProgress(), context.previousOrders);
      }
    },
    
    // On success, refetch to get real data from server
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ordersInProgress() });
      queryClient.invalidateQueries({ queryKey: queryKeys.ordersToBePlace() });
      console.log(' Order created - synced with server');
    },
  });
};

// Mutation: Delete Order - OPTIMISTIC: removes from UI immediately
export const useDeleteOrder = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (orderId: number) => supplyChainService.deleteOrderInProgress(orderId),
    
    // OPTIMISTIC: Remove from UI immediately before API completes
    onMutate: async (orderId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.ordersInProgress() });
      await queryClient.cancelQueries({ queryKey: queryKeys.shipments() });
      
      // Snapshot previous values for rollback
      const previousOrders = queryClient.getQueryData(queryKeys.ordersInProgress());
      const previousShipments = queryClient.getQueryData(queryKeys.shipments());
      
      // Optimistically remove order from cache
      queryClient.setQueryData(queryKeys.ordersInProgress(), (old: any) => {
        const orders = old?.orders || old || [];
        return { orders: orders.filter((o: any) => o.order_id !== orderId) };
      });
      
      // Optimistically remove associated shipments from cache
      queryClient.setQueryData(queryKeys.shipments(), (old: any) => {
        const shipments = old?.shipments || old || [];
        return { shipments: shipments.filter((s: any) => s.order_id !== orderId) };
      });
      
      console.log('⚡ Optimistic: Order removed from UI instantly');
      return { previousOrders, previousShipments };
    },
    
    // If API fails, rollback to previous state
    onError: (err, orderId, context) => {
      console.error('❌ API failed, rolling back:', err);
      if (context?.previousOrders) {
        queryClient.setQueryData(queryKeys.ordersInProgress(), context.previousOrders);
      }
      if (context?.previousShipments) {
        queryClient.setQueryData(queryKeys.shipments(), context.previousShipments);
      }
    },
    
    // On success, refetch to ensure sync with server
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ordersInProgress() });
      queryClient.invalidateQueries({ queryKey: queryKeys.shipments() });
      queryClient.invalidateQueries({ queryKey: queryKeys.ordersToBePlace() });
      console.log('Order deleted - synced with server');
    },
  });
};

// Mutation: Create Shipments - OPTIMISTIC: shows placeholder immediately
export const useCreateShipments = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (orderId: number) => supplyChainService.createShipmentsFromOrder(orderId),
    
    // OPTIMISTIC: Add placeholder shipments immediately
    onMutate: async (orderId) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.shipments() });
      
      const previousShipments = queryClient.getQueryData(queryKeys.shipments());
      
      // Get the order to create placeholder shipments
      const ordersData: any = queryClient.getQueryData(queryKeys.ordersInProgress());
      const orders = ordersData?.orders || ordersData || [];
      const order = orders.find((o: any) => o.order_id === orderId);
      
      if (order?.allocations) {
        // Create placeholder shipments for each allocation
        const placeholderShipments = order.allocations.map((alloc: any, idx: number) => ({
          shipment_id: Date.now() + idx,
          order_id: orderId,
          order_number: order.order_number,
          sku: order.sku,
          origin: 'CN',
          destination: alloc.warehouse,
          quantity: alloc.final_quantity || alloc.quantity,
          status: 'pending',
          created_at: new Date().toISOString(),
        }));
        
        queryClient.setQueryData(queryKeys.shipments(), (old: any) => {
          const shipments = old?.shipments || old || [];
          return { shipments: [...shipments, ...placeholderShipments] };
        });
      }
      
      console.log('⚡ Optimistic: Shipments added to UI instantly');
      return { previousShipments };
    },
    
    onError: (err, orderId, context) => {
      console.error('❌ API failed, rolling back:', err);
      if (context?.previousShipments) {
        queryClient.setQueryData(queryKeys.shipments(), context.previousShipments);
      }
    },
    
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.shipments() });
      queryClient.invalidateQueries({ queryKey: queryKeys.ordersInProgress() });
    },
  });
};

// Mutation: Update Shipment - Simple cache invalidation (more stable)
export const useUpdateShipment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ shipmentId, data }: { shipmentId: number; data: any }) => 
      supplyChainService.updateShipment(shipmentId, data),
    
    // On success, invalidate cache to refresh from server
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.shipments() });
      console.log('✏️ Shipment updated - cache invalidated');
    },
    
    // On error, just log it
    onError: (err) => {
      console.error('❌ Shipment update failed:', err);
    },
  });
};

// Mutation: Delete Shipment - OPTIMISTIC: removes from UI immediately
export const useDeleteShipment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (shipmentId: number) => supplyChainService.deleteShipment(shipmentId),
    
    onMutate: async (shipmentId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.shipments() });
      
      // Snapshot previous value for rollback
      const previousShipments = queryClient.getQueryData(queryKeys.shipments());
      
      // Optimistically remove shipment from cache
      queryClient.setQueryData(queryKeys.shipments(), (old: any) => {
        const shipments = old?.shipments || old || [];
        return { shipments: shipments.filter((s: any) => s.shipment_id !== shipmentId) };
      });
      
      console.log('⚡ Optimistic: Shipment removed from UI instantly');
      return { previousShipments };
    },
    
    onError: (err, shipmentId, context) => {
      console.error('❌ API failed, rolling back:', err);
      if (context?.previousShipments) {
        queryClient.setQueryData(queryKeys.shipments(), context.previousShipments);
      }
    },
    
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.shipments() });
      console.log(' Shipment deleted - synced with server');
    },
  });
};

// Hook to manually invalidate order management cache
export const useInvalidateOrderManagement = () => {
  const queryClient = useQueryClient();
  
  return {
    invalidateAll: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ordersInProgress() });
      queryClient.invalidateQueries({ queryKey: queryKeys.shipments() });
      queryClient.invalidateQueries({ queryKey: queryKeys.ordersToBePlace() });
      console.log('All order management cache invalidated');
    },
    invalidateOrders: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ordersInProgress() });
      queryClient.invalidateQueries({ queryKey: queryKeys.ordersToBePlace() });
    },
    invalidateShipments: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.shipments() });
    },
  };
};

// Mutations for data modification
export const useClearCache = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: () => supplyChainService.clearCache(),
    onSuccess: () => {
      // Clear all queries from the cache
      queryClient.invalidateQueries();
      console.log('🗑️ All query cache cleared');
    },
  });
};

// Custom hook for batch prefetching commonly used data
export const usePrefetchSupplyChainData = () => {
  const queryClient = useQueryClient();
  
  const prefetchWarehouseData = async (warehouse: string) => {
    // Prefetch multiple related queries in parallel
    const prefetchPromises = [
      queryClient.prefetchQuery({
        queryKey: queryKeys.rawDataCombined(warehouse),
        queryFn: () => supplyChainService.getRawDataCombined(warehouse),
        ...queryConfig.rawData,
      }),
      queryClient.prefetchQuery({
        queryKey: queryKeys.localStockManagement(warehouse),
        queryFn: () => supplyChainService.getLocalStockManagement(warehouse),
        ...queryConfig.calculatedData,
      }),
    ];
    
    await Promise.all(prefetchPromises);
  };
  
  const prefetchAllCommonData = async () => {
    // Prefetch central and orders data that's commonly accessed
    const prefetchPromises = [
      queryClient.prefetchQuery({
        queryKey: queryKeys.centralStockManagement(),
        queryFn: () => supplyChainService.getCentralStockManagement(),
        ...queryConfig.calculatedData,
      }),
      queryClient.prefetchQuery({
        queryKey: queryKeys.ordersToBePlace(),
        queryFn: () => supplyChainService.getOrdersToBePlaced(),
        ...queryConfig.realTimeData,
      }),
    ];
    
    await Promise.all(prefetchPromises);
  };
  
  return { prefetchWarehouseData, prefetchAllCommonData };
};

// Custom hook for cache management
export const useCacheManager = () => {
  const queryClient = useQueryClient();
  
  const getCacheStats = () => {
    const queryCache = queryClient.getQueryCache();
    const queries = queryCache.getAll();
    
    const stats = {
      totalQueries: queries.length,
      activeQueries: queries.filter(q => q.state.status === 'pending').length,
      successQueries: queries.filter(q => q.state.status === 'success').length,
      errorQueries: queries.filter(q => q.state.status === 'error').length,
      cacheSize: queryCache.getAll().reduce((size, query) => {
        return size + (query.state.data ? JSON.stringify(query.state.data).length : 0);
      }, 0),
    };
    
    return stats;
  };
  
  const clearSpecificCache = (patterns: string[]) => {
    patterns.forEach(pattern => {
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === pattern
      });
    });
  };
  
  return { getCacheStats, clearSpecificCache };
};
